import React from 'react';
import { Shield } from 'lucide-react';
import { useBrandSettings } from '../context/BrandSettingsContext';

/**
 * Shared brand-mark component. Renders the admin-configured brand logo image
 * (logos.default from BrandSettings) with a Shield fallback.
 *
 * Two visual variants:
 *   default  - image in a rounded box (or gradient-shield-in-box fallback). For page headers.
 *   plain    - bare image (or bare colored Shield fallback). For footers / empty states.
 *
 * Sizes (map applies to both variants):
 *   xs         - w-8  (mobile / compact)
 *   sm         - w-9  (small header)
 *   md         - w-10 (desktop header, DEFAULT)
 *   lg         - w-12 (hero) / plain w-8
 *   responsive - w-8 sm:w-10 (mobile→desktop adaptive header)
 *
 * Uses the localStorage-hydrated logos.default from BrandSettingsContext,
 * so it does NOT flash the Shield fallback on subsequent page loads.
 */
const SIZES = {
  xs:         { box: 'w-8 h-8',                   icon: 'w-4 h-4',                  plain: 'w-5 h-5' },
  sm:         { box: 'w-9 h-9',                   icon: 'w-5 h-5',                  plain: 'w-6 h-6' },
  md:         { box: 'w-10 h-10',                 icon: 'w-6 h-6',                  plain: 'w-6 h-6' },
  lg:         { box: 'w-12 h-12',                 icon: 'w-7 h-7',                  plain: 'w-8 h-8' },
  responsive: { box: 'w-8 h-8 sm:w-10 sm:h-10',   icon: 'w-5 h-5 sm:w-6 sm:h-6',    plain: 'w-6 h-6 sm:w-8 sm:h-8' },
};

const BrandLogo = ({
  size = 'md',
  variant = 'default',
  className = '',
  alt = 'KaiCheckIt',
  'data-testid': testId,
  ...rest
}) => {
  const { logos } = useBrandSettings();
  const logoUrl = logos?.default;
  const s = SIZES[size] || SIZES.md;

  // Plain variant: image (or colored Shield) with no gradient container. Used in footers
  // and empty-state placeholders.
  if (variant === 'plain') {
    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={alt}
          className={`${s.plain} rounded object-contain ${className}`.trim()}
          data-testid={testId}
          {...rest}
        />
      );
    }
    return (
      <Shield
        className={`${s.plain} text-blue-600 dark:text-blue-400 ${className}`.trim()}
        data-testid={testId}
        {...rest}
      />
    );
  }

  // Default variant: image in rounded box, or gradient-shield-in-box fallback. Used in headers.
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={alt}
        className={`${s.box} rounded-xl object-contain ${className}`.trim()}
        data-testid={testId}
        {...rest}
      />
    );
  }
  return (
    <div
      className={`${s.box} bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg ${className}`.trim()}
      data-testid={testId}
      {...rest}
    >
      <Shield className={`${s.icon} text-white`} />
    </div>
  );
};

export default BrandLogo;
